import { useState } from "react";
import { copyText, type CopySource } from "@/lib/copy";
import { useLibrary, toggleSnippet } from "@/lib/library";
import { applyDecoration } from "@/lib/decorate-combo";
import { applyStyle, FANCY_STYLES, STYLE_CATEGORIES } from "@/lib/text-generators";

// Every /font-generator font category EXCEPT "Symbols & Frames" (those are
// decorations, not letter fonts) — used by the per-card "Decorate" button to
// render the user's text in each font inside the frame.
const FONT_GROUPS = STYLE_CATEGORIES.filter((c) => c.id !== "symbols")
  .map((c) => ({ category: c, styles: FANCY_STYLES.filter((s) => s.category === c.id) }))
  .filter((g) => g.styles.length > 0);

interface CopyComboGridProps {
  combos: string[];
  /** Analytics source bucket for the copy. */
  source?: CopySource;
  /**
   * "combo" (default) = ornamental symbols in the symbol-rich font.
   * "face" = text emoticons (Lenny faces) in the normal text font, with wider
   *   cards so long faces aren't clipped — the combining marks position best in
   *   a regular sans font, not a symbol/coding font.
   */
  layout?: "combo" | "face";
  /**
   * When true, show a text field; whatever the user types is wrapped inside
   * every decoration (border = centered middle line, flourish = mirrored both
   * sides) and copying copies that personalised version. Empty field = the
   * plain ready-made combos, exactly as before.
   */
  wrapText?: boolean;
  /** Noun for the text-field hint, e.g. "border" or "flourish". */
  wrapNoun?: string;
  /** Optional one-line caption per combo, keyed by the raw (un-wrapped) combo string. */
  descriptions?: Record<string, string>;
}

/**
 * Grid of copy-as-a-whole-string cards. Each card copies its full string
 * (including the newline in two-line borders) on click and flashes "Copied!";
 * the ★ button saves the string to the Saved snippets list (/my-symbols).
 * Used by /decorative/$slug, /kaomoji/$slug and /lenny-faces.
 */
export function CopyComboGrid({ combos, source = "decorative-combo", layout = "combo", wrapText = false, wrapNoun = "design", descriptions }: CopyComboGridProps) {
  const { savedSnippets } = useLibrary();
  const [copied, setCopied] = useState<number | null>(null);
  const [text, setText] = useState("");
  const [decorateId, setDecorateId] = useState<number | null>(null);
  const hasText = text.trim().length > 0;

  async function handleCopy(value: string, i: number) {
    const ok = await copyText(value, { source });
    if (ok) {
      setCopied(i);
      window.setTimeout(() => setCopied((c) => (c === i ? null : c)), 1200);
    }
  }

  const gridCols = layout === "face" ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3";

  return (
    <>
      {wrapText && (
        <div className="mb-5 max-w-xl">
          <label htmlFor="decorate-text" className="block text-sm font-medium text-foreground">
            Add your text
          </label>
          <div className="mt-2 flex items-center gap-2">
            <input
              id="decorate-text"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a name, title or quote…"
              autoComplete="off"
              className="w-full rounded-lg border border-border bg-surface/60 px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground focus-visible:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {text && (
              <button
                type="button"
                onClick={() => setText("")}
                className="flex-none rounded-lg border border-border bg-surface/60 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Your text is wrapped in each {wrapNoun} below — tap one to copy it. Leave empty to copy the plain decorations.
          </p>
        </div>
      )}
      <ul className={`grid grid-cols-1 gap-3 ${gridCols}`}>
      {combos.map((rawCombo, i) => {
        // Faces (kaomoji / Lenny) are placed on both sides unchanged; frames
        // (borders/brackets) mirror the right side so glyphs face inward.
        const combo = wrapText ? applyDecoration(rawCombo, text, layout !== "face") : rawCombo;
        // Two-line borders/banners need monospace so the top and bottom rows
        // stay aligned; everything else gets the symbol-rich combo font (which
        // covers ornamental glyphs a coding font would render as tofu).
        const multiline = combo.includes("\n");
        const saved = savedSnippets.includes(combo);
        const plain = combo.replace(/\n/g, " ");
        const caption = descriptions?.[rawCombo];
        return (
          <li key={i} className="relative min-w-0">
            <button
              type="button"
              onClick={() => handleCopy(combo, i)}
              aria-label={`Copy ${plain}`}
              className="glass group relative flex min-h-[5.5rem] w-full min-w-0 items-center justify-center overflow-hidden rounded-xl px-4 py-5 text-center transition-colors hover:border-primary/40"
            >
              <span
                className={
                  // Pinned to the card width so a long ornament can never widen
                  // the grid track (which would clip under the page overflow guard).
                  // Two-line borders keep their alignment and scroll horizontally if
                  // too wide; single-line flourishes (incl. "add your text" output)
                  // wrap, preserving their internal spaces.
                  "block max-w-full text-foreground/90 " +
                  (multiline
                    ? "overflow-x-auto whitespace-pre font-mono text-sm leading-snug sm:text-base"
                    : layout === "face"
                      ? "whitespace-pre-wrap break-words font-sans text-base leading-relaxed sm:text-lg"
                      : "whitespace-pre-wrap break-words font-combo text-lg leading-relaxed tracking-wide sm:text-xl")
                }
              >
                {combo}
              </span>
              <span
                className={
                  "pointer-events-none absolute left-2 top-2 rounded-md bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground transition-opacity " +
                  (copied === i ? "opacity-100" : "opacity-0")
                }
                aria-hidden="true"
              >
                Copied!
              </span>
              <span
                className={
                  "pointer-events-none absolute left-2 top-2 text-[11px] text-muted-foreground transition-opacity " +
                  (copied === i ? "opacity-0" : "opacity-0 group-hover:opacity-100")
                }
                aria-hidden="true"
              >
                Click to copy
              </span>
            </button>
            <button
              type="button"
              onClick={() => toggleSnippet(combo)}
              aria-pressed={saved}
              aria-label={saved ? `Remove "${plain}" from saved snippets` : `Save "${plain}" to snippets`}
              title={saved ? "Saved — click to remove" : "Save to snippets"}
              className={
                "absolute right-2 top-2 z-10 rounded-md px-1.5 py-0.5 text-lg leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                (saved ? "text-amber-400" : "text-muted-foreground hover:text-foreground")
              }
            >
              {saved ? "★" : "☆"}
            </button>
            {wrapText && (
              <button
                type="button"
                onClick={() => setDecorateId((d) => (d === i ? null : i))}
                disabled={!hasText}
                aria-expanded={decorateId === i}
                title={hasText ? "Style your text in every font, inside this frame" : "Add your text above first"}
                className={
                  "mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-border bg-surface/50 px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 " +
                  (decorateId === i ? "border-primary/50 text-foreground" : "text-muted-foreground")
                }
              >
                <span aria-hidden="true">✨</span> Decorate
              </button>
            )}
            {wrapText && decorateId === i && hasText && (
              <ComboDecoratePanel rawCombo={rawCombo} text={text} source={source} noun={wrapNoun} layout={layout} />
            )}
            {caption && (
              <p className="mt-1.5 px-1 text-center text-xs leading-snug text-muted-foreground">{caption}</p>
            )}
          </li>
        );
      })}
      </ul>
    </>
  );
}

/**
 * Per-frame "Decorate" panel: renders the user's text inside THIS frame in every
 * /font-generator font (all categories except Symbols & Frames), grouped by
 * category. Each row is tap-to-copy. Mobile-safe: the list scrolls vertically,
 * and each preview wraps (single-line) or scrolls horizontally (two-line borders)
 * so nothing ever forces the page sideways.
 */
function ComboDecoratePanel({ rawCombo, text, source, noun, layout }: { rawCombo: string; text: string; source: CopySource; noun: string; layout: "combo" | "face" }) {
  const [copied, setCopied] = useState<string | null>(null);
  const isFace = layout === "face";

  async function copy(id: string, value: string) {
    const ok = await copyText(value, { source });
    if (!ok) return;
    setCopied(id);
    window.setTimeout(() => setCopied((c) => (c === id ? null : c)), 1200);
  }

  return (
    <div className="mt-2 rounded-lg border border-dashed border-border bg-surface/30 p-2.5 sm:p-3">
      <p className="mb-2 text-xs text-muted-foreground">
        Your text in every font, inside this {noun} — tap any to copy.
      </p>
      <div className="max-h-[22rem] space-y-3 overflow-y-auto overflow-x-hidden pr-1">
        {FONT_GROUPS.map((g) => (
          <div key={g.category.id}>
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{g.category.name}</h4>
            <ul className="mt-1 grid grid-cols-1 gap-1">
              {g.styles.map((st) => {
                const value = applyDecoration(rawCombo, applyStyle(st, text), !isFace);
                const multiline = value.includes("\n");
                const isCopied = copied === st.id;
                return (
                  <li key={st.id}>
                    <button
                      type="button"
                      onClick={() => copy(st.id, value)}
                      aria-label={`Copy ${st.name} style`}
                      className="group flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border bg-surface/50 px-2.5 py-1.5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">{st.name}</span>
                        <span
                          className={
                            "mt-0.5 block max-w-full text-foreground/90 " +
                            (multiline
                              ? "overflow-x-auto whitespace-pre font-mono text-xs leading-snug"
                              : isFace
                                ? "whitespace-pre-wrap break-words font-sans text-sm leading-relaxed"
                                : "whitespace-pre-wrap break-words font-combo text-sm leading-relaxed")
                          }
                        >
                          {value}
                        </span>
                      </span>
                      <span
                        className={
                          "flex-none text-[10px] uppercase tracking-wider " +
                          (isCopied ? "text-accent" : "text-muted-foreground group-hover:text-foreground")
                        }
                        aria-hidden="true"
                      >
                        {isCopied ? "✓" : "copy"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
